<template>
  <div class="fee-query-page">
    <!-- 顶部工具栏 -->
    <a-card class="toolbar-card" :bordered="false">
      <div class="toolbar-row">
        <div class="toolbar-left">
          <div class="page-title">{{ canConfirmPayment ? '财务专用缴费查询' : '缴费查询' }}</div>
          <div class="page-sub muted">快速定位房号并查看账单，自动加载欠费数</div>
        </div>

        <div class="toolbar-right">
          <a-button @click="goBatchExport">
            批量导出账单
          </a-button>
          <a-button type="primary" :loading="loadingAll" @click="refreshArrears">
            刷新欠费数
          </a-button>
        </div>
      </div>

      <div class="filter-row">
        <a-input-search
          v-model="keyword"
          allowClear
          placeholder="搜索：楼号 / 房号 / 业主姓名（如：1-101 / 101 / 张三）"
          style="max-width: 520px;"
          @search="onSearch"
        />
        <a-checkbox v-model="onlyUnpaid" style="margin-left: 12px;">
          仅看欠费户
        </a-checkbox>

        <div class="muted" style="margin-left: auto;">
          已加载 {{ ownersData.length }} 户
          <span v-if="arrearsOwnerCount != null"> · 欠费 {{ arrearsOwnerCount }} 户</span>
        </div>
      </div>
    </a-card>

    <!-- 主体 -->
    <a-card class="content-card" :bordered="false">
      <a-spin :spinning="loading">
        <div v-if="!loading && filteredOwners.length === 0" class="empty">
          <div class="empty-title">没有匹配的业主</div>
          <div class="muted">试试换个关键词，或取消"仅看欠费户"。</div>
        </div>

        <div v-else class="building-grid">
          <a-card
            v-for="b in buildingList"
            :key="b.building"
            class="building-card"
            :bordered="false"
          >
            <div class="building-head">
              <div class="building-name">{{ b.building }}号楼</div>
              <div class="building-meta muted">
                {{ b.owners.length }} 户
                <span v-if="b.unpaidTotal > 0"> · 欠费 {{ b.unpaidTotal }} 笔</span>
              </div>
            </div>

            <div class="room-grid">
              <div
                v-for="owner in b.owners"
                :key="String(owner.id)"
                class="room-pill"
                @click="showOwnerBills(owner)"
              >
                <div class="pill-top">
                  <span class="room-no">{{ owner.room }}</span>
                  <a-badge
                    v-if="(pendingCounts[owner.id] || 0) > 0"
                    :count="pendingCounts[owner.id]"
                    :overflowCount="99"
                  />
                </div>
                <div class="owner-name muted ellipsis">{{ owner.owner_name || "-" }}</div>
              </div>
            </div>
          </a-card>
        </div>
      </a-spin>
    </a-card>

    <!-- 账单详情弹窗 -->
    <a-modal
      v-model="billModalVisible"
      :title="modalTitle"
      :width="900"
      :footer="null"
      @cancel="closeBillModal"
      destroyOnClose
    >
      <div class="modal-actions">
        <a-button @click="copyBillInfo">生成并复制催缴信息</a-button>
      </div>

      <a-card class="wallet-card" :bordered="false">
        <div class="wallet-metrics">
          <div class="wallet-item">
            <div class="wallet-label">历史余额</div>
            <div class="wallet-value">¥{{ Number(walletBalance || 0).toFixed(2) }}</div>
          </div>
          <div class="wallet-item">
            <div class="wallet-label">电费未付累计</div>
            <div class="wallet-value">¥{{ Number(unpaidTotalElectric || 0).toFixed(2) }}</div>
          </div>
          <div class="wallet-item">
            <div class="wallet-label">水费未付累计</div>
            <div class="wallet-value">¥{{ Number(unpaidTotalWater || 0).toFixed(2) }}</div>
          </div>
          <div class="wallet-item">
            <div class="wallet-label">物业未付累计</div>
            <div class="wallet-value">¥{{ Number(unpaidTotalProperty || 0).toFixed(2) }}</div>
          </div>
          <div class="wallet-item">
            <div class="wallet-label">租金未付累计</div>
            <div class="wallet-value">¥{{ Number(unpaidTotalRent || 0).toFixed(2) }}</div>
          </div>
          <div class="wallet-item">
            <div class="wallet-label">未付合计</div>
            <div class="wallet-value muted-strong">¥{{ Number(unpaidTotalAmount || 0).toFixed(2) }}</div>
          </div>
          <div class="wallet-item">
            <div class="wallet-label">差值</div>
            <div class="wallet-value" :class="balanceGap >= 0 ? 'ok' : 'danger'">
              ¥{{ Number(balanceGap || 0).toFixed(2) }}
            </div>
          </div>
        </div>
        <div class="wallet-entry-row">
          <a-input-number
            v-model="entryAmount"
            :precision="2"
            :step="0.01"
            class="entry-amount"
            placeholder="金额"
          />
          <a-date-picker
            v-model="entryBusinessDate"
            class="entry-date"
            placeholder="业务日期"
          />
          <a-select
            v-model="entryPaymentMethod"
            class="entry-method"
            placeholder="付款方式"
            allowClear
          >
            <a-select-option v-for="m in paymentMethodOptions" :key="m" :value="m">{{ m }}</a-select-option>
          </a-select>
          <a-input
            v-model="entryRemark"
            class="entry-remark"
            placeholder="备注（可选）"
            allowClear
          />
          <a-button type="primary" class="entry-submit" :loading="submittingEntry" @click="submitPaymentEntry">
            录入缴费
          </a-button>
        </div>
      </a-card>

      <a-alert
        v-if="billCopyMessage"
        :message="billCopyMessage"
        :type="billMessageType === 'error' ? 'error' : 'success'"
        show-icon
        style="margin-bottom: 12px"
      />

      <div v-if="sortedBills.length === 0" class="muted">暂无账单</div>

      <div v-else class="bill-list">
        <a-card
          v-for="bill in sortedBills"
          :key="bill.bill_type + '-' + bill.id"
          class="bill-card"
          :bordered="false"
        >
          <div class="bill-top">
            <div>
              <div class="bill-period">{{ bill.bill_type_label }}</div>
              <div class="muted">{{ bill.period_start }} ~ {{ bill.period_end }}</div>
            </div>

            <div class="bill-right">
              <div class="bill-amount">
                ¥{{ Number(bill.amount || 0).toFixed(2) }}
              </div>

              <a-tag :color="bill.is_paid ? 'green' : 'orange'">
                {{ bill.is_paid ? "已支付" : "未支付" }}
              </a-tag>
            </div>
          </div>

          <a-divider style="margin: 12px 0" />

          <div class="bill-detail-grid">
            <template v-if="bill.bill_type === 'property'">
              <div><strong>期数：</strong>第 {{ bill.detail.period_number }} 期</div>
              <div><strong>应收日期：</strong>{{ bill.detail.due_date || "-" }}</div>
              <div><strong>租金：</strong>¥{{ Number(bill.detail.rent_amount || 0).toFixed(2) }}</div>
              <div><strong>物业费：</strong>¥{{ Number(bill.detail.property_fee_amount || 0).toFixed(2) }}</div>
            </template>
            <template v-else>
              <div><strong>用量：</strong>{{ bill.detail.total_kwh || 0 }} {{ bill.detail.unit_name || '' }}</div>
              <div><strong>单价：</strong>{{ bill.detail.unit_price || 0 }} 元</div>
            </template>
          </div>

          <div v-if="canConfirmPayment && !bill.is_paid" class="bill-footer">
            <a-button type="primary" @click="showConfirmPaymentModal(bill)">
              收取确认
            </a-button>
          </div>
        </a-card>
      </div>
    </a-modal>

    <!-- 收取确认弹窗（共用组件） -->
    <BillPaymentConfirmModal
      :visible="confirmPaymentModalVisible"
      :bill="currentConfirmBill"
      :account-id="currentOwner && currentOwner.id"
      :mark-paid-url="confirmMarkPaidUrl"
      request-id-prefix="fee_query"
      @cancel="closeConfirmPaymentModal"
      @success="onConfirmPaymentSuccess"
    />
  </div>
</template>

<script>
import request from "@/utils/request";
import moment from "moment";
import BillPaymentConfirmModal from "@/components/owner_payment/BillPaymentConfirmModal.vue";
export default {
  name: "FeeQuery",
  components: { BillPaymentConfirmModal },
  data() {
    return {
      loading: false,
      ownersData: [],
      pendingCounts: {},
      arrearsOwnerCount: null,

      teamId: null,
      communityName: "",

      keyword: "",
      onlyUnpaid: false,

      billModalVisible: false,
      modalTitle: "",
      modalBills: [],
      currentOwner: null,

      confirmPaymentModalVisible: false,
      currentConfirmBill: null,

      billCopyMessage: "",
      billMessageType: "success",

      loadingAll: false,
      walletBalance: 0,
      unpaidTotalElectric: 0,
      unpaidTotalWater: 0,
      unpaidTotalProperty: 0,
      unpaidTotalRent: 0,
      unpaidTotalAmount: 0,
      balanceGap: 0,
      entryAmount: null,
      entryBusinessDate: null,
      entryRemark: "",
      entryPaymentMethod: undefined,
      paymentMethodOptions: ["骁垒建行", "骁垒兴业", "扫码", "现金"],
      submittingEntry: false,
    };
  },
  computed: {
    canConfirmPayment() {
      return !!(this.$route && this.$route.meta && this.$route.meta.canConfirmPayment);
    },

    filteredOwners() {
      const kw = (this.keyword || "").trim().toLowerCase();
      const like = (val) => String(val || "").toLowerCase().includes(kw);
      let list = (this.ownersData || []).slice();

      if (kw) {
        const m = kw.match(/^(\d+)\s*[-#\s]\s*(\w+)$/);
        if (m) {
          const b = m[1];
          const r = m[2];
          list = list.filter((o) => String(o.building) === b && String(o.room).includes(r));
        } else {
          list = list.filter((o) => like(o.building) || like(o.room) || like(o.owner_name));
        }
      }

      if (this.onlyUnpaid) {
        list = list.filter((o) => (this.pendingCounts[o.id] || 0) > 0);
      }

      return list;
    },

    buildingList() {
      const map = {};
      this.filteredOwners.forEach((o) => {
        const b = String(o.building || "").trim();
        if (!map[b]) map[b] = [];
        map[b].push(o);
      });

      return Object.keys(map)
        .sort((a, b) => Number(a) - Number(b) || a.localeCompare(b))
        .map((b) => {
          const owners = map[b].slice().sort((x, y) => this.compareRoom(x.room, y.room));
          const unpaidTotal = owners.reduce((s, o) => s + (this.pendingCounts[o.id] || 0), 0);
          return { building: b, owners, unpaidTotal };
        });
    },

    sortedBills() {
      const list = (this.modalBills || []).slice();
      list.sort((a, b) => {
        const ap = a.is_paid ? 1 : 0;
        const bp = b.is_paid ? 1 : 0;
        if (ap !== bp) return ap - bp;
        return String(a.period_start || "").localeCompare(String(b.period_start || ""));
      });
      return list;
    },

    confirmMarkPaidUrl() {
      if (!this.currentConfirmBill || !this.currentConfirmBill.id) return "";
      return `${this.getBillApiPath(this.currentConfirmBill)}${this.currentConfirmBill.id}/mark_paid/`;
    },
  },

  async mounted() {
    await this.fetchMyTeam();
    this.loadOwnersData();
    this.loadArrearsSummary();
  },

  methods: {
    getBillApiPath(bill) {
      if (bill.bill_type === "electric") return "/electricity/bills/";
      if (bill.bill_type === "water") return "/electricity/water-bills/";
      return "/electricity/property-bills/";
    },

    getRoomSortKey(room) {
      let raw = String(room || "").trim().toUpperCase();
      if (raw.includes("/")) raw = raw.split("/")[0].trim();
      if (!raw) return [999999, 999999, 9, 999999, 999999, ""];

      const parseNumericMain = (numStr) => {
        const s = String(numStr || "").trim();
        if (!/^\d+$/.test(s)) return { floor: 999999, unit: 999999 };
        if (s.length >= 3) {
          const floor = parseInt(s.slice(0, -2), 10);
          const unit = parseInt(s.slice(-2), 10);
          return {
            floor: Number.isFinite(floor) ? floor : 999999,
            unit: Number.isFinite(unit) ? unit : 999999,
          };
        }
        const unit = parseInt(s, 10);
        return { floor: 1, unit: Number.isFinite(unit) ? unit : 999999 };
      };

      const lettersToCode = (letters) => {
        const L = String(letters || "").toUpperCase();
        let code = 0;
        for (let i = 0; i < L.length; i++) {
          const c = L.charCodeAt(i);
          if (c < 65 || c > 90) continue;
          code = code * 26 + (c - 64);
        }
        return code || 999999;
      };

      if (/^\d+$/.test(raw)) {
        const { floor, unit } = parseNumericMain(raw);
        return [floor, unit, 0, 0, 0, raw];
      }

      let m = raw.match(/^(\d+)\s*([A-Z]+)$/);
      if (m) {
        const { floor, unit } = parseNumericMain(m[1]);
        return [floor, unit, 1, lettersToCode(m[2]), 0, raw];
      }

      m = raw.match(/^(\d+)\s*-\s*(\d+)$/);
      if (m) {
        const { floor, unit } = parseNumericMain(m[1]);
        const sub = parseInt(m[2], 10);
        return [floor, unit, 2, Number.isFinite(sub) ? sub : 999999, 0, raw];
      }

      m = raw.match(/^(\d+)\s*([A-Z]+)\s*(\d+)?$/);
      if (m) {
        const floor = parseInt(m[1], 10);
        const letterCode = lettersToCode(m[2]);
        const num = m[3] ? parseInt(m[3], 10) : 0;
        return [Number.isFinite(floor) ? floor : 999999, 999998, 1, letterCode, Number.isFinite(num) ? num : 0, raw];
      }

      m = raw.match(/^([A-Z]+)$/);
      if (m) return [1, 999998, 1, lettersToCode(m[1]), 0, raw];

      m = raw.match(/^([A-Z]+)\s*-\s*(\d+)$/);
      if (m) {
        const sub = parseInt(m[2], 10);
        return [1, 999998, 1, lettersToCode(m[1]), Number.isFinite(sub) ? sub : 0, raw];
      }

      return [999999, 999999, 9, 999999, 999999, raw];
    },

    compareRoom(aRoom, bRoom) {
      const ka = this.getRoomSortKey(aRoom);
      const kb = this.getRoomSortKey(bRoom);
      for (let i = 0; i < Math.max(ka.length, kb.length); i++) {
        const va = ka[i];
        const vb = kb[i];
        if (va === vb) continue;
        if (typeof va === "string" || typeof vb === "string") {
          return String(va).localeCompare(String(vb), "zh");
        }
        return (va || 0) - (vb || 0);
      }
      return 0;
    },

    async fetchMyTeam() {
      try {
        const res = await request({ url: "/manage_data/my_team/", method: "get" });
        const data = res && res.data ? res.data : res;
        this.teamId = data.team_id;
        this.communityName = data.community_name || "";
      } catch (e) {
        console.error(e);
        this.teamId = null;
        this.communityName = "";
        this.toast("获取当前用户 team_id 失败，请重新登录或联系管理员", "error");
      }
    },

    toast(msg, type = "info") {
      if (this.$message && this.$message[type]) this.$message[type](msg);
    },

    onSearch() {
      if (this.keyword && this.keyword.trim()) this.toast("已按关键词筛选", "success");
    },

    async loadOwnersData() {
      this.loading = true;
      try {
        const res = await request({
          url: "/electricity/accounts/",
          method: "get",
          params: { page_size: 9999 },
        });
        const data = res && res.data ? res.data : res;
        const list = Array.isArray(data) ? data : (data?.results || []);
        this.ownersData = list.map(item => ({
          ...item,
          building: item.building_no,
          room: item.unit_no,
          phone: item.contact_phone,
          area: item.floor_area,
        }));
      } catch (e) {
        console.error(e);
        this.toast("网络错误，请检查网络连接", "error");
      } finally {
        this.loading = false;
      }
    },

    async loadArrearsSummary() {
      try {
        const res = await request({
          url: "/electricity/accounts/arrears-summary/",
          method: "get",
        });
        const data = res && res.data ? res.data : res;
        this.arrearsOwnerCount = data.arrears_owner_count || 0;
        const details = data.details || {};
        const counts = {};
        for (const [key, val] of Object.entries(details)) {
          counts[Number(key)] = val;
        }
        this.pendingCounts = counts;
      } catch (e) {
        console.error(e);
        this.toast("加载欠费摘要失败", "error");
      }
    },

    async refreshArrears() {
      this.loadingAll = true;
      try {
        await this.loadArrearsSummary();
        const total = Object.values(this.pendingCounts).reduce((s, v) => s + (Number(v) || 0), 0);
        this.toast(`刷新完成，共 ${this.arrearsOwnerCount} 户欠费，${total} 笔待缴`, "success");
      } finally {
        this.loadingAll = false;
      }
    },

    async showOwnerBills(owner) {
      try {
        if (!this.teamId) {
          this.toast("无法获取 team_id，请刷新或重新登录", "error");
          return;
        }

        this.currentOwner = owner;

        const res = await request({
          url: `/electricity/accounts/${owner.id}/all-bills/`,
          method: "get",
        });

        const data = res && res.data ? res.data : res;
        const bills = data.bills || [];
        this.walletBalance = Number(data.wallet_balance || 0);
        this.unpaidTotalElectric = Number(
          data.unpaid_total_electric != null ? data.unpaid_total_electric : 0
        );
        this.unpaidTotalWater = Number(
          data.unpaid_total_water != null ? data.unpaid_total_water : 0
        );
        this.unpaidTotalProperty = Number(
          data.unpaid_total_property != null ? data.unpaid_total_property : 0
        );
        this.unpaidTotalRent = Number(
          data.unpaid_total_rent != null ? data.unpaid_total_rent : 0
        );
        this.unpaidTotalAmount = Number(data.unpaid_total_amount || 0);
        this.balanceGap = Number(data.balance_gap || 0);

        this.modalBills = bills.map((b) => ({
          ...b,
          building: owner.building,
          room: owner.room,
          owner_name: owner.owner_name,
        }));

        if (data.unpaid_count != null) {
          this.$set(this.pendingCounts, owner.id, data.unpaid_count);
        }

        this.modalTitle = `${owner.building}号楼${owner.room}室 · ${owner.owner_name || ""}`;
        this.billModalVisible = true;
      } catch (e) {
        console.error(e);
        this.toast("网络错误，请重试", "error");
      }
    },

    closeBillModal() {
      this.billModalVisible = false;
      this.billCopyMessage = "";
      this.currentOwner = null;
      this.modalBills = [];
      this.walletBalance = 0;
      this.unpaidTotalElectric = 0;
      this.unpaidTotalWater = 0;
      this.unpaidTotalProperty = 0;
      this.unpaidTotalRent = 0;
      this.unpaidTotalAmount = 0;
      this.balanceGap = 0;
      this.entryAmount = null;
      this.entryBusinessDate = null;
      this.entryRemark = "";
      this.entryPaymentMethod = undefined;
    },

    goBatchExport() {
      if (this.$router) {
        this.$router.push({ path: "/owner_mg/bill_batch_export" });
      }
    },

    showConfirmPaymentModal(bill) {
      if (!bill) return;
      const amount = Number(bill.amount || 0);
      const currentBalance = Number(this.walletBalance || 0);
      const balanceAfter = Number((currentBalance - amount).toFixed(2));

      this.currentConfirmBill = {
        id: bill.id,
        bill_type: bill.bill_type,
        bill_type_label: bill.bill_type_label,
        building: bill.building,
        room: bill.room,
        ownerName: bill.owner_name,
        period_start: bill.period_start,
        period_end: bill.period_end,
        amount,
        current_balance: currentBalance,
        balance_after: balanceAfter,
        due_date: bill.detail ? bill.detail.due_date : null,
        rent_amount: bill.detail ? Number(bill.detail.rent_amount || 0) : 0,
        property_fee_amount: bill.detail ? Number(bill.detail.property_fee_amount || 0) : 0,
        detail_kwh: bill.detail ? bill.detail.total_kwh : null,
        detail_unit: bill.detail ? bill.detail.unit_name : null,
        detail_price: bill.detail ? bill.detail.unit_price : null,
      };

      if (!this.currentConfirmBill.id) {
        this.toast("该账单缺少 id，无法进行收取确认", "error");
        return;
      }

      this.confirmPaymentModalVisible = true;
    },

    closeConfirmPaymentModal() {
      this.confirmPaymentModalVisible = false;
      this.currentConfirmBill = null;
    },

    async onConfirmPaymentSuccess() {
      this.toast("收取确认成功：已标记为已支付", "success");
      const b = this.currentConfirmBill;
      if (b && b.id) {
        const id = Number(b.id);
        this.modalBills = (this.modalBills || []).map((p) =>
          Number(p.id) === id && p.bill_type === b.bill_type ? { ...p, is_paid: true } : p
        );
      }
      await this.loadArrearsSummary();
      if (this.currentOwner && this.currentOwner.id) {
        await this.showOwnerBills(this.currentOwner);
      }
      this.closeConfirmPaymentModal();
    },

    async submitPaymentEntry() {
      if (!this.currentOwner || !this.currentOwner.id) {
        return this.toast("当前业主信息无效", "error");
      }
      const amount = Number(this.entryAmount);
      if (!Number.isFinite(amount) || amount === 0) {
        return this.toast("请输入非 0 金额", "error");
      }
      if (!this.entryPaymentMethod) {
        return this.toast("请选择付款方式", "error");
      }
      const businessDate = this.entryBusinessDate
        ? this.entryBusinessDate.format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD");

      this.submittingEntry = true;
      try {
        await request({
          url: `/owner-payment/accounts/${this.currentOwner.id}/entries/`,
          method: "post",
          data: {
            amount: amount.toFixed(2),
            business_date: businessDate,
            remark: this.entryRemark || "",
            payment_method: this.entryPaymentMethod,
            request_id: `manual_entry_${Date.now()}_${this.currentOwner.id}`,
          },
        });
        this.toast("缴费录入成功", "success");
        this.entryAmount = null;
        this.entryBusinessDate = null;
        this.entryRemark = "";
        this.entryPaymentMethod = undefined;
        if (this.currentOwner && this.currentOwner.id) {
          await this.showOwnerBills(this.currentOwner);
        }
      } catch (e) {
        const errMsg = e.response?.data?.detail || e.response?.data?.error || "缴费录入失败";
        this.toast(errMsg, "error");
      } finally {
        this.submittingEntry = false;
      }
    },

    async copyBillInfo() {
      try {
        if (!this.teamId) {
          return this.showBillCopyMessage("无法获取 team_id，请刷新页面或重新登录", "error");
        }

        if (!this.currentOwner) {
          return this.showBillCopyMessage("当前账单信息无效", "error");
        }

        const { building, room } = this.currentOwner;
        const pad2 = (n) => String(n).padStart(2, "0");
        const now = new Date();
        const fee_period =
          `${now.getFullYear()}年${now.getMonth() + 1}月${now.getDate()}日` +
          `${pad2(now.getHours())}点${pad2(now.getMinutes())}分${pad2(now.getSeconds())}秒`;

        const resp = await request({
          url: "/electricity/property-bills/get-owner-page-url/",
          method: "post",
          data: {
            building: String(building || "").trim(),
            room: String(room || "").trim(),
          },
        });

        const result = resp && resp.data ? resp.data : resp;
        if (!result || !result.success) {
          return this.showBillCopyMessage(result?.error || "获取缴费链接失败", "error");
        }

        const baseUrl = window.location.origin;
        const pageUrl = `${baseUrl}${result.page_url}`;

        const ownerName =
          (result && result.owner_info && result.owner_info.owner_name) ? String(result.owner_info.owner_name).trim() : "";

        const namePart = ownerName ? `尊敬的${ownerName}` : "尊敬的客户";

        const bracket =
          process.env.VUE_APP_SMS_BRACKET_LABEL || "中暨大厦";
        const sms = `【${bracket}】${namePart}：截止到${fee_period}您的缴费订单已生成，请点击${pageUrl} 完成缴费，如有疑问请咨询本客服。如已缴费请忽略此信息，祝您生活愉快！`;

        await navigator.clipboard.writeText(sms);
        this.showBillCopyMessage("缴费短信内容已复制到剪贴板", "success");
      } catch (e) {
        console.error(e);
        this.showBillCopyMessage("生成或复制失败", "error");
      }
    },

    showBillCopyMessage(msg, type) {
      this.billCopyMessage = msg;
      this.billMessageType = type || "success";
      setTimeout(() => (this.billCopyMessage = ""), 5000);
    },
  },
};
</script>

<style scoped>
.fee-query-page {
  padding: 16px;
}

.toolbar-card,
.content-card {
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0,0,0,0.06);
}

.toolbar-card {
  margin-bottom: 16px;
}

.toolbar-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.page-title {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.2;
}
.page-sub {
  margin-top: 6px;
}

.toolbar-right {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
}

.building-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.building-card {
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0,0,0,0.04);
  border: 1px solid rgba(0,0,0,0.05);
}

.building-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
}

.building-name {
  font-size: 16px;
  font-weight: 800;
}

.room-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
}

.room-pill {
  cursor: pointer;
  border-radius: 14px;
  padding: 10px 12px;
  background: #fafafa;
  border: 1px solid rgba(0,0,0,0.06);
  transition: all 0.18s ease;
}
.room-pill:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(0,0,0,0.08);
  background: #fff;
}

.pill-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.room-no {
  font-weight: 900;
  font-size: 16px;
}

.owner-name {
  margin-top: 6px;
  font-size: 12px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
}

.bill-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.bill-card {
  border-radius: 14px;
  border: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

.bill-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.bill-period {
  font-size: 16px;
  font-weight: 900;
}

.bill-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}

.bill-amount {
  font-size: 18px;
  font-weight: 900;
}

.bill-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
  font-size: 13px;
}

.bill-footer {
  margin-top: 12px;
  text-align: right;
}

.confirm-box .danger {
  color: #d4380d;
  font-weight: 900;
}
.confirm-box .ok {
  color: #52c41a;
  font-weight: 900;
}
.wallet-card {
  margin-bottom: 12px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fafafa;
}
.wallet-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
}
.muted-strong {
  color: rgba(0, 0, 0, 0.75);
  font-weight: 700;
}
.wallet-item {
  padding: 6px 8px;
}
.wallet-label {
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}
.wallet-value {
  font-size: 18px;
  font-weight: 800;
}
.wallet-value.ok {
  color: #52c41a;
}
.wallet-value.danger {
  color: #d4380d;
}
.wallet-entry-row {
  margin-top: 12px;
  display: flex;
  gap: 6px;
  flex-wrap: nowrap;
  align-items: center;
  width: 100%;
}
.wallet-entry-row .entry-amount {
  width: 110px;
  flex: 0 0 110px;
}
.wallet-entry-row .entry-date {
  width: 130px;
  flex: 0 0 130px;
}
.wallet-entry-row .entry-method {
  width: 100px;
  flex: 0 0 100px;
}
.wallet-entry-row .entry-remark {
  flex: 1 1 auto;
  min-width: 80px;
  max-width: 200px;
}
.wallet-entry-row .entry-submit {
  flex: 0 0 auto;
}

.empty {
  padding: 24px 0;
  text-align: center;
}
.empty-title {
  font-size: 16px;
  font-weight: 800;
  margin-bottom: 6px;
}

.muted {
  color: rgba(0,0,0,0.45);
}

.ellipsis {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
